// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Request detail view showing per-agent track cards with findings.
 */

import { useCallback, useRef, useState, type FC } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, FileDown, Play, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui'
import { toast } from 'sonner'
import type { NodeApiClient } from '@/app-shell/services'
import type { Agent } from '@/subsystems/agents'
import { usePipelineRunner } from '@/applications/shared'
import { documentKeys } from '@/subsystems/documents'
import type { SrmRequest } from '../types'
import { TrackCards } from './TrackCards'
import { ExportReportDialog } from './ExportReportDialog'

interface RequestDetailProps {
  request: SrmRequest
  agents: Agent[]
  onBack: () => void
  apiClient: NodeApiClient
  orgId: string
  wsId: string
  onRefresh?: () => void
}

export const RequestDetail: FC<RequestDetailProps> = ({
  request,
  agents,
  onBack,
  apiClient,
  orgId,
  wsId,
  onRefresh,
}) => {
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  // Tracks run independently, so a set of agent ids — not a single one.
  const [runningAgentIds, setRunningAgentIds] = useState<Set<string>>(() => new Set())
  const [agentErrors, setAgentErrors] = useState<Record<string, string | null>>({})
  const jobToAgentRef = useRef<Record<string, string>>({})
  const refreshAnalysisStatusRef = useRef<(() => void) | null>(null)
  const queryClient = useQueryClient()

  const { run, isRunning, activeJobs } = usePipelineRunner({
    apiClient,
    orgId,
    wsId,
    appId: 'srm',
    // Intentionally no onRefresh — refetching findings/requests/documents on
    // every status tick (PENDING → RUNNING → terminal) thrashes the API while
    // the pipeline is still running. Refresh once on completion below.
    onAllComplete: ({ erroredJobIds }) => {
      // All tracked jobs are terminal, so nothing is running anymore.
      setRunningAgentIds(new Set())
      // Refetch only what the run actually changes: document pipeline_status
      // (drives the per-doc badges) and renderer findings datasets. The
      // /srm/requests metadata is unchanged by a pipeline run, so don't
      // bounce off it. Use refetchQueries (not invalidate) so observed
      // panels re-fire their dataset GETs deterministically.
      void queryClient.refetchQueries({ queryKey: documentKeys.all, type: 'all' })
      void queryClient.refetchQueries({
        queryKey: ['renderer-slot-data-multi'],
        type: 'all',
      })
      refreshAnalysisStatusRef.current?.()
      if (erroredJobIds.length > 0) {
        const errored = new Set(
          erroredJobIds
            .map((jobId) => jobToAgentRef.current[jobId])
            .filter((id): id is string => Boolean(id)),
        )
        if (errored.size > 0) {
          setAgentErrors((prev) => {
            const next = { ...prev }
            for (const agentId of errored) {
              next[agentId] = 'Analysis failed. Check the job logs for details.'
            }
            return next
          })
        }
      }
    },
  })

  const handleRunTrack = useCallback(
    async (target: 'all' | Agent) => {
      // 'all' runs every agent's track; each agent only processes the documents
      // in its own slots. A pipeline fetches exactly the document_ids it's given,
      // so the per-agent scoping must happen here, not on the backend.
      const docsForAgent = (agent: Agent): string[] => {
        const slotIds = new Set(agent.slots.map((s) => s.id))
        return request.documents
          .filter((d) => d.slotId && slotIds.has(d.slotId))
          .map((d) => d.documentId)
      }

      const runs = (target === 'all' ? agents : [target])
        .map((agent) => ({ agent, documentIds: docsForAgent(agent) }))
        .filter((r) => r.documentIds.length > 0)

      if (runs.length === 0) {
        toast.error('No documents', {
          description:
            target === 'all' ? 'Upload documents first' : `No documents for ${target.label}`,
        })
        return
      }

      const agentsBeingRun = runs.map((r) => r.agent)
      const description = `Running ${agentsBeingRun.map((a) => a.label.toLowerCase()).join(', ')}`

      // Clear prior errors for the agents being re-run so the panel reverts
      // to its loading/results state until this run finishes.
      setAgentErrors((prev) => {
        const next = { ...prev }
        for (const a of agentsBeingRun) next[a.agentId] = null
        return next
      })
      setRunningAgentIds((prev) => {
        const next = new Set(prev)
        for (const a of agentsBeingRun) next.add(a.agentId)
        return next
      })

      try {
        await Promise.all(
          runs.map(async ({ agent, documentIds }) => {
            const result = await run(documentIds, {
              agentId: agent.agentId,
              groupId: request.groupId,
            })
            // Capture jobId → agentId so onAllComplete can attribute errors.
            for (const job of result.jobs) {
              jobToAgentRef.current[job.jobId] = agent.agentId
            }
          }),
        )
        toast.success('Analysis started', { description })
      } catch (err) {
        toast.error('Failed to start analysis', {
          description: err instanceof Error ? err.message : 'Unknown error',
        })
        setRunningAgentIds((prev) => {
          const next = new Set(prev)
          for (const a of agentsBeingRun) next.delete(a.agentId)
          return next
        })
      }
    },
    [request.documents, request.groupId, agents, run],
  )

  return (
    <div className="space-y-6" data-testid="srm-detail">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="gap-2 shrink-0"
            aria-label="Back to request list"
            data-testid="srm-back"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold truncate">
              {request.groupId}
              {request.externalRef && (
                <span className="ml-2 text-base font-normal text-muted-foreground">{request.externalRef}</span>
              )}
            </h1>
            {request.title && (
              <p className="text-sm text-muted-foreground truncate">{request.title}</p>
            )}
            {request.uploadedBy && (
              <p className="text-xs sm:text-sm text-muted-foreground">Submitted by {request.uploadedBy}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0 pl-12 sm:pl-0">
          <Button size="sm" disabled={isRunning || activeJobs.length > 0} onClick={() => handleRunTrack('all')} data-testid="srm-run-all">
            {isRunning || activeJobs.length > 0 ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            {isRunning || activeJobs.length > 0 ? 'Running...' : 'Run All'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setExportDialogOpen(true)} data-testid="srm-export-report">
            <FileDown className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      <TrackCards
        agents={agents}
        documents={request.documents}
        groupId={request.groupId}
        apiClient={apiClient}
        orgId={orgId}
        wsId={wsId}
        onDocumentChange={onRefresh ?? (() => {})}
        onRunAgent={(agent) => handleRunTrack(agent)}
        runningAgentIds={runningAgentIds}
        agentErrors={agentErrors}
        refreshAnalysisStatusRef={refreshAnalysisStatusRef}
      />

      <ExportReportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        request={request}
        agents={agents}
        apiClient={apiClient}
        orgId={orgId}
        wsId={wsId}
      />
    </div>
  )
}
